import '../../scripts/custom-event-polyfill';
import { eventContextChanged } from './cam-context-switcher-constants';

let _currentState = new Map();

class CamContextSwitcherDatastore {

  constructor() {
    _currentState.set('userName', null);
    _currentState.set('selectedOrganization', null);
    _currentState.set('selectedCostcenter', null);
    _currentState.set('selectedWorkspace', null);
    _currentState.set('personalWorkspace', null);
    _currentState.set('displayOrganizationMap', new Map());
    _currentState.set('displayCostcenterMap', new Map());
    _currentState.set('scopesResponse', null);
  }

  initializeData(data, userName, selectedWorkspaceId, config) {
    this.apiUrl = config.apiUrl.replace('/+$', '');
    _currentState.set('userName', userName);
    _currentState.set('scopesResponse', data);
    _currentState.set('personalWorkspace', _currentState.get('scopesResponse').workspaces.find((ws) => {
      return this._isPersonalWorkspace(ws);
    }));
    if (selectedWorkspaceId) {
      this.changeWorkspace(selectedWorkspaceId);
    }
    if (!_currentState.get('selectedWorkspace') && _currentState.get('personalWorkspace')) {
      this.changeWorkspace(_currentState.get('personalWorkspace').id);
    }
  }

  _fireContextChangedEvent() {
    let eventOptions = {
      detail: {
        organization: _currentState.get('selectedOrganization'),
        costcenter: _currentState.get('selectedCostcenter'),
        workspace: _currentState.get('selectedWorkspace')
      },
      bubbles: true,
      cancelable: false
    };

    let camContextSwitcher = document.querySelector('#cam-context-switcher');
    camContextSwitcher.dispatchEvent(new CustomEvent(eventContextChanged, eventOptions));
  }

  _isPersonalWorkspace(workspace) {
    return workspace.type === 'personal_workspace' && workspace.id.match(`^${this.userName}\\d*$`) !== null;
  }

  get personalWorkspace() {
    return _currentState.get('personalWorkspace');
  }

  get organizations() {

    let displayOrganizationList = this._sortByAttr(_currentState.get('scopesResponse').organizations, 'name');

    displayOrganizationList = displayOrganizationList.map((org) => {
      return {
        organization: org,
        display: this.displayOrganization(org)
      };
    });

    return displayOrganizationList;
  }

  get costcenters() {
    let organizationId = _currentState.get('selectedOrganization') ? _currentState.get('selectedOrganization').id : null;

    let filteredCostcenterList = _currentState.get('scopesResponse').costcenters;
    if (organizationId !== null) {
      filteredCostcenterList = filteredCostcenterList.filter((cc) => {
        return cc.organizations.includes(organizationId);
      });
    }
    filteredCostcenterList = this._sortByAttr(filteredCostcenterList, 'name');

    let displayCostcenterList = filteredCostcenterList.map((cc) => {
      return {
        costcenter: cc,
        display: this.displayCostcenter(cc)
      };
    });

    return displayCostcenterList;
  }

  get workspaces() {
    let organizationId = _currentState.get('selectedOrganization') ? _currentState.get('selectedOrganization').id : null;
    let costcenterId = _currentState.get('selectedCostcenter') ? _currentState.get('selectedCostcenter').id : null;

    let filteredWorkspaceList = _currentState.get('scopesResponse').workspaces;
    if (organizationId !== null) {
      filteredWorkspaceList = filteredWorkspaceList.filter((ws) => {
        return ws.organizations.includes(organizationId);
      });
    }
    if (costcenterId !== null) {
      filteredWorkspaceList = filteredWorkspaceList.filter((ws) => {
        return ws.costcenter === costcenterId;
      });
    }

    let displayWorkspaceList = [];
    if (filteredWorkspaceList.includes(_currentState.get('personalWorkspace'))) {
      displayWorkspaceList.push({
        workspace: _currentState.get('personalWorkspace'),
        display: this.displayWorkspace(_currentState.get('personalWorkspace'))
      });
    }
    filteredWorkspaceList = filteredWorkspaceList.filter((ws) => ws.type !== 'personal_workspace');
    filteredWorkspaceList = this._sortByAttr(filteredWorkspaceList, 'name');
    displayWorkspaceList = displayWorkspaceList.concat(filteredWorkspaceList.map((ws) => {
      return {
        workspace: ws,
        display: this.displayWorkspace(ws)
      };
    }));

    return displayWorkspaceList;
  }

  displayOrganization(organization, workspace=null) {
    let orgId = organization ? organization.id : workspace.organizations[0];
    if (_currentState.get('displayOrganizationMap').has(orgId)) {
      if (!((_currentState.get('selectedOrganization') && orgId === _currentState.get('selectedOrganization').id)
          || _currentState.get('displayOrganizationMap').get(orgId).selected)) {
        return _currentState.get('displayOrganizationMap').get(orgId);
      }
    }

    organization = organization ? organization : _currentState.get('scopesResponse').organizations.find((org) => org.id === workspace.organizations[0]);

    let displayOrganizationItem;
    if (!organization) {
      displayOrganizationItem = {
        name: _currentState.get('selectedWorkspace').organizations[0],
        iconUrl: this._formatIconUrl(null, 'organization'),
        selected: false
      };
    } else {
      displayOrganizationItem = {
        name: organization.name,
        iconUrl: this._formatIconUrl(organization.icon, 'organization'),
        selected: _currentState.get('selectedOrganization') && organization.id === _currentState.get('selectedOrganization').id
      };
    }

    _currentState.get('displayOrganizationMap').set(orgId, displayOrganizationItem);
    return displayOrganizationItem;

  }

  displayCostcenter(costcenter, workspace=null) {
    let ccId = costcenter ? costcenter.id : workspace.costcenter;
    if (_currentState.get('displayCostcenterMap').has(ccId)) {
      if (!((_currentState.get('selectedCostcenter') && ccId === _currentState.get('selectedCostcenter').id)
          || _currentState.get('displayCostcenterMap').get(ccId).selected)) {
        return _currentState.get('displayCostcenterMap').get(ccId);
      }
    }

    costcenter = costcenter ? costcenter : _currentState.get('scopesResponse').costcenters.find((cc) => cc.id === workspace.costcenter);

    let displayCostcenterItem;
    if (!costcenter) {
      displayCostcenterItem = {
        name: `Default ${workspace.organizations[0]} Cost Center`,
        selected: false
      };
    } else {
      displayCostcenterItem = {
        name: costcenter.name,
        selected: _currentState.get('selectedCostcenter') && costcenter.id === _currentState.get('selectedCostcenter').id
      };
    }

    _currentState.get('displayCostcenterMap').set(ccId, displayCostcenterItem);
    return displayCostcenterItem;
  }

  displayWorkspace(workspace) {
    let iconType = 'workspace';
    if (workspace === _currentState.get('personalWorkspace')) {
      iconType = 'user';
    }
    let isPersonal = this._isPersonalWorkspace(workspace);
    return {
      name: isPersonal ? 'My Workspace' : workspace.name,
      iconUrl: this._formatIconUrl(workspace.icon, iconType),
      selected: _currentState.get('selectedWorkspace') && workspace.id === _currentState.get('selectedWorkspace').id,
      personal: isPersonal,
      organization: this.displayOrganization(null, workspace).name,
      costcenter: this.displayCostcenter(null, workspace).name
    };
  }

  _formatIconUrl(relativePath, iconType) {
    var imageSource;
    if (!relativePath || relativePath === null) {
      let defaultIcon;
      switch (iconType) {
      case 'user':
        defaultIcon = '/images/user-nophoto-lg.png'; break;
      case 'workspace':
        defaultIcon = '/images/workspace-nophoto-lg.png'; break;
      case 'organization':
        defaultIcon = '/images/org-nophoto.png'; break;
      default:
        defaultIcon = '/images/workspace-nophoto-lg.png'; break;
      }
      imageSource = this.apiUrl + defaultIcon;
    } else if (relativePath === 'none') {
      imageSource = '';
    } else if (relativePath.match(/^http:\/\//) || relativePath.match(/^https:\/\//)) {
      imageSource = relativePath;
    } else {
      if (relativePath.match(/^\//)) {
        imageSource = this.apiUrl + relativePath;
      } else {
        imageSource = this.apiUrl + '/' + relativePath;
      }
    }
    return imageSource;
  }

  _sortByAttr(unsortedArray, attrName) {
    let sortedArray = unsortedArray.sort((a, b) => {
      let aAttr = a[attrName].toLowerCase();
      let bAttr = b[attrName].toLowerCase();
      return (aAttr < bAttr) ? -1 : ((aAttr > bAttr) ? 1 : 0);
    });
    return sortedArray;
  }

  get selectedOrganization() {
    return _currentState.get('selectedOrganization');
  }

  get selectedCostcenter() {
    return _currentState.get('selectedCostcenter');
  }

  get selectedWorkspace() {
    return _currentState.get('selectedWorkspace');
  }

  changeOrganization(organizationId) {
    let updatedOrganization = _currentState.get('scopesResponse').organizations.find((org) => {
      return org.id === organizationId;
    }) || null ;
    if (_currentState.get('selectedOrganization') && organizationId !== _currentState.get('selectedOrganization').id) {
      this.changeCostcenter(null);
    }
    _currentState.set('selectedOrganization', updatedOrganization);
  }

  changeCostcenter(costcenterId) {
    let updatedCostcenter = _currentState.get('scopesResponse').costcenters.find((cc) => {
      return cc.id === costcenterId;
    }) || null;
    _currentState.set('selectedCostcenter', updatedCostcenter);
    if (updatedCostcenter) {
      this.changeOrganization(updatedCostcenter.organizations[0]);
    }
  }

  changeWorkspace(workspaceId) {
    if (_currentState.get('selectedWorkspace') && workspaceId === _currentState.get('selectedWorkspace')) {
      return;
    }
    let updatedWorkspace = _currentState.get('scopesResponse').workspaces.find((ws) => {
      return ws.id === workspaceId;
    });
    if (updatedWorkspace) {
      _currentState.set('selectedWorkspace', updatedWorkspace);
      this.changeCostcenter(updatedWorkspace.costcenter);
      if (!_currentState.get('selectedOrganization')) {
        this.changeOrganization(updatedWorkspace.organizations[0]);
      }
    }
    this._fireContextChangedEvent();
  }

  get userName() {
    return _currentState.get('userName');
  }

}

export default new CamContextSwitcherDatastore();



// WEBPACK FOOTER //
// ./src/cam-context-switcher/scripts/cam-context-switcher-datastore.js